import { getAccessSnapshot } from "../context/AccessControl/accessSnapshot";

/**
 * ¿Puede esta persona ver este elemento del menú?
 *
 * WHY existe separado de `<Can>`: `<Can>` arrastra un vocabulario heredado
 * orientado a componentes ("user-modal:editProfile") que convive con el catálogo
 * real del backend. El menú ya NO habla ese idioma: cada elemento declara su
 * permiso del catálogo ("flowbuilder:ver"), así que aquí no hay heurística que
 * adivine de qué vocabulario es una clave. Una sola fuente, una sola decisión.
 *
 * WHY la misma función decide el elemento Y su grupo: antes el grupo se decidía
 * por `profile` y el elemento por `<Can>`. Cuando las dos discrepaban salía una
 * cabecera que abría sobre una lista vacía. Con una única función eso no puede
 * volver a ocurrir: si ningún elemento pasa, el grupo no se dibuja.
 */
export const puedeVerItemDeMenu = (item, usuario) => {
  // Sin candado: información sobre uno mismo o ayuda del producto (Mi equipo,
  // Ayuda). No pertenecen a ningún módulo del catálogo y nunca fueron
  // administrativos.
  if (!item || !item.permission) return true;

  const snapshot = getAccessSnapshot();

  // Camino normal: manda el rol. `can()` ya concede todo al ámbito plataforma.
  if (snapshot.loaded) return snapshot.can(item.permission);

  /**
   * Modo degradado: `/me/permissions` no contestó.
   *
   * La carga de permisos nunca puede impedir trabajar, así que se recurre a la
   * marca `fallback`, que reproduce EXACTAMENTE el reparto anterior
   * (`drawer-admin-items:view` → 'admin', `drawer-service-items:view` →
   * 'service'). Un panel sin backend de permisos se comporta como el de
   * siempre, ni más abierto ni más cerrado.
   */
  if (esElevado(usuario)) return true;
  return item.fallback !== "admin";
};

/** Administrador de empresa o superusuario de plataforma. */
export const esElevado = usuario =>
  usuario?.profile === "admin" ||
  usuario?.super === true ||
  usuario?.super === 1 ||
  String(usuario?.super) === "1";

/**
 * ¿Se cumple la condición de plan o de empresa de un elemento del menú?
 * Extraído de AccordionMenu (doc 0446) para que el menú simple y la paleta Ctrl+K
 * decidan exactamente igual que el menú clásico.
 */
export const cumpleCondicionDeMenu = (conditional, usuario) => {
  if (!conditional) return true;

  let result = false;
  switch (conditional) {
    case 'showKanban':
      result = localStorage.getItem('showKanban') === 'true';
      break;
    case 'showSchedules':
      result = localStorage.getItem('showSchedules') === 'true';
      break;
    case 'showInternalChat':
      result = localStorage.getItem('showInternalChat') === 'true';
      break;
    case 'showCampaigns':
      result = localStorage.getItem('showCampaigns') === 'true';
      break;
    case 'showOpenAi':
      result = localStorage.getItem('showOpenAi') === 'true';
      break;
    case 'showIntegrations':
      result = localStorage.getItem('showIntegrations') === 'true';
      break;
    case 'showExternalApi':
      result = localStorage.getItem('showExternalApi') === 'true';
      break;
    case 'showFiles':
      result = localStorage.getItem('showFiles') === 'true';
      break;
    // La empresa trabaja con subagrupaciones (doc 0380 D7 · paso 2.A).
    //
    // No sale de `localStorage` como los del plan: es un ajuste por empresa y
    // viaja en el snapshot de acceso, que se puede recargar sin volver a
    // entrar. Así el menú reacciona en cuanto se guarda el interruptor.
    case 'teamsEnabled':
      result = getAccessSnapshot().teamsEnabled === true;
      break;
    case 'user.super':
      result =
        usuario &&
        (usuario.super === true || usuario.super === 1 || String(usuario.super) === "1");
      break;
    case 'user.superOrCompanyAdmin':
      result =
        usuario &&
        (usuario.super === true ||
          usuario.super === 1 ||
          String(usuario.super) === "1" ||
          usuario.profile === "admin");
      break;
    default:
      result = true;
  }
  
  return result;
};

/** Visibilidad completa de un elemento: condición del plan + permiso del rol. */
export const itemDeMenuVisible = (item, usuario) =>
  cumpleCondicionDeMenu(item?.conditional, usuario) && puedeVerItemDeMenu(item, usuario);
